import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import styled from "styled-components/macro";

export const SectionTitle = styled.h2`
  font-size: 1.2em;
  margin-top: 0;
  margin-bottom: 10px;
  color: white;
`;

export const BarButton = styled.button`
  background: #0080ff;
  border: none;
  border-radius: 20px;
  padding: 7px 12px;
  color: white;
  outline: unset;
  margin-bottom: 5px;
  margin-right: 7px;
  :hover {
    background: #1d77d0;
    outline: unset;
  }
  :active {
    background: #3e9dfb;
    outline: unset;
  }
`;

export const BarInput = styled.input`
  background: #3a4348;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  outline: unset;
  width: calc(100% - 10px);
  :focus {
    box-shadow: #0080ff 0 0 6px 1px;
    outline: unset;
  }
`;

export const SelectInput = styled.select`
  background: #3a4348;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  outline: unset;
  width: calc(100% - 10px);
  :focus {
    box-shadow: #0080ff 0 0 6px 1px;
    outline: unset;
  }
`;

export const InputLabel = styled.div`
  margin-top: 8px;
  font-size: 13px;
  color: #adadad;
`;

export const BarPropContainer = styled.div`
  margin-bottom: 8px;
`;

export const BarTextProperty = observer(
  (props: {
    name: string;
    value: string;
    onChange: (newValue: string) => void;
  }): JSX.Element => (
    <BarPropContainer>
      <InputLabel>{props.name}:</InputLabel>
      <BarInput
        value={props.value}
        onChange={(event) => props.onChange(event.currentTarget.value)}
      />
    </BarPropContainer>
  )
);

export const BarNumberProperty = observer(
  (props: {
    name: string;
    max?: number;
    min?: number;
    value: number;
    onChange: (newValue: number) => void;
  }): JSX.Element => (
    <BarPropContainer>
      <InputLabel>{props.name}:</InputLabel>
      <BarInput
        type="number"
        max={props.max?.toString()}
        min={props.min?.toString()}
        value={props.value.toString()}
        onChange={(event) =>
          props.onChange(parseInt(event.currentTarget.value))
        }
      />
    </BarPropContainer>
  )
);

export interface BarSelectOption {
  value: string;
  label: string;
}

export const BarSelectProperty = observer(
  (props: {
    name: string;
    value: string;
    options: BarSelectOption[];
    onChange: (newValue: string) => void;
  }): JSX.Element => {
    return (
      <BarPropContainer>
        <InputLabel>{props.name}:</InputLabel>
        <SelectInput
          value={props.value}
          onChange={(event) => props.onChange(event.currentTarget.value)}
        >
          {props.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </SelectInput>
      </BarPropContainer>
    );
  }
);

export const SelectProperty = observer(
  <T extends string>(props: {
    name: string;
    value: T;
    option: Record<T, string>;
    onChange: (newValue: T) => void;
  }): JSX.Element => {
    return (
      <BarPropContainer>
        <InputLabel>{props.name}:</InputLabel>
        <SelectInput
          value={props.value}
          onChange={(event) => props.onChange(event.currentTarget.value as T)}
        >
          {Object.keys(props.option).map((k) => (
            <option value={k}>{props.option[k as T]}</option>
          ))}
        </SelectInput>
      </BarPropContainer>
    );
  }
);

const BarCheckboxLabel = styled.label`
  display: inline-block;
  padding-right: 10px;
  white-space: nowrap;
`;

export const BarBoolProperty = observer(
  (props: {
    name: string;
    value: boolean;
    onChange: (newValue: boolean) => void;
  }): JSX.Element => (
    <BarPropContainer>
      <BarCheckboxLabel>
        <input
          type="checkbox"
          checked={props.value}
          onChange={(event) => props.onChange(event.currentTarget.checked)}
          style={{ verticalAlign: "middle" }}
        />{" "}
        <span style={{ verticalAlign: "middle" }}>{props.name}</span>
      </BarCheckboxLabel>
    </BarPropContainer>
  )
);

const BarTagContainer = styled.div`
  background-color: #007fff;
  border-radius: 5px;
  padding: 3px 5px;
  margin-right: 5px;
  font-size: 14px;
  white-space: nowrap;
`;

const BarTag = observer((props: { value: string; onDelete: () => void }) => (
  <BarTagContainer>
    {props.value} <FontAwesomeIcon icon={faTimes} onClick={props.onDelete} />
  </BarTagContainer>
));

const TagInputContainer = styled.div`
  background: #3a4348;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  outline: unset;
  width: calc(100% - 10px);
  display: flex;
  :focus-within {
    box-shadow: #0080ff 0 0 6px 1px;
    outline: unset;
  }
`;

const TagInput = styled.input`
  background: none;
  outline: unset;
  border: none;
  padding: none;
  margin: none;
  color: inherit;
  width: 100%;
  min-width: 50px;
`;

export const BarTagsProperty = observer(
  (props: { name: string; value: string[] }): JSX.Element => {
    const [newTag, setNewTag] = useState("");

    const handleAddTag = () => {
      const trimmedTag = newTag.trim();
      if (trimmedTag !== "") {
        props.value.push(trimmedTag);
        setNewTag("");
      }
    };

    return (
      <BarPropContainer>
        <InputLabel>{props.name}:</InputLabel>
        <TagInputContainer>
          {props.value.map((item, i) => (
            <BarTag value={item} onDelete={() => props.value.splice(i, 1)} />
          ))}
          <TagInput
            value={newTag}
            onChange={(e) => setNewTag(e.currentTarget.value)}
            onBlur={handleAddTag}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault(); // prevents accidental form submission
                handleAddTag();
              }
            }}
          />
        </TagInputContainer>
      </BarPropContainer>
    );
  }
);
